/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `InboxView` for the Caintra Connect frontend.
 * @module components/Chat/InboxView
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import styles from "../../styles/Chat.module.css";
import { ReactComponent as OnlineIcon } from "../../assets/icons/Chat/OnlineIcon.svg";
import { ReactComponent as OfflineIcon } from "../../assets/icons/Chat/OfflineIcon.svg";
import Caintra_Logo_03 from "../../assets/Caintra_Logo_03.png";
import {
  getFilteredChats,
  isUserOnline,
  getUnreadChatsCount,
} from "../../logic/chat";

const InboxView = ({
  chats,
  searchQuery,
  users,
  companies,
  selectedChat,
  activeConnections,
  onChatSelect,
  onViewChange,
  activeView,
  connectionRequests,
  className,
}) => {
  const { t } = useTranslation();

  const filteredChats = getFilteredChats(chats, searchQuery, users, companies);

  return (
    <div className={`${styles.inboxContainer} ${className || ""}`}>
      <div className={styles.inboxHeader}>
        <div className={styles.viewToggleButtons}>
          <button
            className={`${styles.viewToggleButton} ${
              activeView === "inbox" ? styles.activeButton : ""
            }`}
            onClick={() => onViewChange("inbox")}
          >
            <span>{t("pages.chat.inbox")}</span>
            {getUnreadChatsCount(chats) > 0 && (
              <span className={styles.requestBadge}>
                {getUnreadChatsCount(chats)}
              </span>
            )}
          </button>
          <button
            className={`${styles.viewToggleButton} ${
              activeView === "requests" ? styles.activeButton : ""
            }`}
            onClick={() => onViewChange("requests")}
          >
            <span>{t("pages.chat.requests")}</span>
            {connectionRequests.length > 0 && (
              <span className={styles.requestBadge}>
                {connectionRequests.length}
              </span>
            )}
          </button>
        </div>
      </div>
      <div className={styles.messagesListContainer}>
        {filteredChats.length > 0 ? (
          filteredChats.map((chat) => {
            const currentUserId = localStorage.getItem("userId");
            const otherUserId =
              chat.userAId === currentUserId ? chat.userBId : chat.userAId;
            const matchedUser = users.find((user) => user._id === otherUserId);
            const company = matchedUser?.RFC
              ? companies[matchedUser.RFC]
              : null;

            return (
              <div
                key={chat.PK}
                className={styles.messageContainer}
                onClick={() => onChatSelect(chat)}
              >
                <div
                  className={`${styles.messageItem} ${
                    selectedChat?.PK === chat.PK ? styles.selectedChat : ""
                  }`}
                >
                  <div className={styles.messageItemAvatar}>
                    <img
                      className={styles.avatar}
                      src={Caintra_Logo_03}
                      alt={t("common.avatarAlt")}
                    />
                  </div>
                  <div className={styles.messageItemContent}>
                    <div className={styles.messageItemSender}>
                      {company?.nombreComercial || t("common.unknownCompany")}
                    </div>
                    <div className={styles.senderInfo}>
                      {isUserOnline(otherUserId, activeConnections) ? (
                        <OnlineIcon className={styles.onlineIcon} />
                      ) : (
                        <OfflineIcon className={styles.onlineIcon} />
                      )}
                      <span className={styles.senderInfoName}>
                        {matchedUser?.nombre || t("pages.chat.unknownUser")}
                      </span>
                      <span className={styles.senderInfoEmail}>
                        ({matchedUser?.correoElectronico || t("pages.chat.noEmail")})
                      </span>
                    </div>
                    <div className={styles.messageItemText}>
                      {chat.lastMessage || t("pages.chat.noMessagesYet")}
                    </div>
                  </div>
                  {chat.lastMessageSeen === false && (
                    <div className={styles.unreadBadge}>
                      <span>1</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className={styles.noContent}>
            {searchQuery.trim()
              ? t("pages.chat.noSearchResults")
              : t("pages.chat.noMessages")}
          </div>
        )}
      </div>
    </div>
  );
};

export default InboxView;
